import { useEffect, useState } from 'react'
import {
  useAnswerOnCallSwap,
  useAssignOnCall,
  useFillOnCall,
  useOnCallNow,
  useOnCallPlan,
  useRequestOnCallSwap,
  useSetOnCallRotation,
  useUsers
} from '../api/queries'
import type { OnCallPlan, OnCallSwap, OnCallWeek, UserRef } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatShortDate } from '../lib/format'
import { useModuleEnabled } from '../navigation'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from './ui'

/** "5/10-12/10" - mandag til mandag, som vagten løber. */
function weekSpan(week: OnCallWeek) {
  return `${formatShortDate(week.startsAt)}-${formatShortDate(week.endsAt)}`
}

function PhoneLink({ user }: { user: UserRef }) {
  if (!user.phoneNumber) return null
  return (
    <a className="btn btn--sm" href={`tel:${user.phoneNumber.replace(/\s/g, '')}`}>
      📞 {user.phoneNumber}
    </a>
  )
}

/**
 * Én linje på forsiden: hvem har vagten nu (beslutning 120). Skjult, når funktionen er slået fra, eller ingen er sat.
 */
export function OnCallLine() {
  const enabled = useModuleEnabled()('intranet_oncall')
  const { data } = useOnCallNow(enabled)
  const current = data?.current
  if (!enabled || !current?.user) return null

  return (
    <div className="map-notice row" style={{ marginBottom: 16, gap: 10, flexWrap: 'wrap' }}>
      <span>
        📟 <strong>Vagt nu: {current.user.displayName}</strong>
        {data?.next?.user && <span className="muted"> · Næste: {data.next.user.displayName}</span>}
      </span>
      <PhoneLink user={current.user} />
    </div>
  )
}

/**
 * Fanen "Vagt" på intranettet (beslutning 120): hvem har vagten nu, planen et halvt år frem, bytte af egne vagter,
 * og for kontoret rækkefølgen og rettelser af de enkelte uger.
 */
export function OnCallTab() {
  const { data, isLoading, error } = useOnCallPlan(26)
  const { user: me, isIntranetAdmin } = useAuth()
  const [swapping, setSwapping] = useState<OnCallWeek | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return null

  const [current, next] = data.weeks
  const myId = me?.id

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card title="📟 Tilkaldevagt">
        {current?.user ? (
          <div className="stack" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Avatar user={current.user} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{current.user.displayName}</div>
                <div className="tiny muted">
                  Uge {current.weekNumber} · til mandag {formatShortDate(current.endsAt)} kl. 7
                </div>
              </div>
              <PhoneLink user={current.user} />
            </div>
            {next?.user && (
              <div className="small muted">
                Næste: <strong>{next.user.displayName}</strong> fra mandag {formatShortDate(next.startsAt)} kl. 7
              </div>
            )}
          </div>
        ) : (
          <EmptyState>{isIntranetAdmin ? 'Ingen har vagten i denne uge. Sæt den herunder.' : 'Ingen har vagten i denne uge.'}</EmptyState>
        )}
      </Card>

      {notice && <div className="map-notice">{notice}</div>}

      <SwapsCard swaps={data.swaps} myId={myId} onDone={setNotice} />

      {swapping && (
        <SwapForm
          week={swapping}
          plan={data}
          myId={myId}
          onClose={(message) => {
            setSwapping(null)
            if (message) setNotice(message)
          }}
        />
      )}

      <Card title="Vagtplan">
        <div className="tiny muted" style={{ marginBottom: 8 }}>
          Vagten skifter mandag kl. 7. Har du en vagt, du ikke kan tage, så tryk "Byt" og spørg en kollega.
        </div>
        <div className="stack" style={{ gap: 2 }}>
          {data.weeks.map((week) => (
            <WeekRow
              key={week.weekStart}
              week={week}
              isMine={week.user?.id === myId}
              isAdmin={isIntranetAdmin}
              onSwap={() => {
                setNotice(null)
                setSwapping(week)
              }}
            />
          ))}
        </div>
      </Card>

      {isIntranetAdmin && <RotationCard rotation={data.rotation} onDone={setNotice} />}
    </div>
  )
}

function WeekRow({
  week,
  isMine,
  isAdmin,
  onSwap
}: {
  week: OnCallWeek
  isMine: boolean
  isAdmin: boolean
  onSwap: () => void
}) {
  const { data: users } = useUsers()
  const assign = useAssignOnCall()
  const [error, setError] = useState<string | null>(null)
  const active = (users ?? []).filter((u) => u.isActive)

  return (
    <div className="device-row stack" style={{ gap: 4 }}>
      <div className="row row--between" style={{ gap: 10, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 10, minWidth: 0 }}>
          <div style={{ width: 64 }}>
            <div className="small" style={{ fontWeight: 600 }}>Uge {week.weekNumber}</div>
            <div className="tiny muted">{weekSpan(week)}</div>
          </div>
          {week.user ? <Avatar user={week.user} size={28} /> : null}
          {isAdmin ? (
            <select
              value={week.user?.id ?? ''}
              disabled={assign.isPending}
              aria-label={`Vagt i uge ${week.weekNumber}`}
              onChange={async (e) => {
                setError(null)
                try {
                  await assign.mutateAsync({
                    weekStart: week.weekStart,
                    userId: e.target.value ? Number(e.target.value) : null,
                    note: week.note
                  })
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Ugen kunne ikke gemmes.')
                }
              }}
            >
              <option value="">- ingen -</option>
              {week.user && !active.some((u) => u.id === week.user!.id) && (
                <option value={week.user.id}>{week.user.displayName}</option>
              )}
              {active.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
          ) : (
            <span className={`small ${week.user ? '' : 'muted'}`}>{week.user?.displayName ?? 'Ingen sat'}</span>
          )}
          {isMine && <span className="pill">Din vagt</span>}
        </div>
        {isMine && (
          <button type="button" className="btn btn--sm" onClick={onSwap}>
            🔁 Byt
          </button>
        )}
      </div>
      {week.note && <div className="tiny muted">{week.note}</div>}
      {week.warnings.map((warning) => (
        <div key={warning} className="tiny" style={{ color: '#B45309' }}>
          ⚠ {warning}
        </div>
      ))}
      {error && <div className="error-box">{error}</div>}
    </div>
  )
}

/** Spørg en kollega om at tage ugen - eller om at bytte med en af kollegaens egne. */
function SwapForm({
  week,
  plan,
  myId,
  onClose
}: {
  week: OnCallWeek
  plan: OnCallPlan
  myId?: number
  onClose: (message?: string) => void
}) {
  const { data: users } = useUsers()
  const request = useRequestOnCallSwap()
  const [targetId, setTargetId] = useState<number | ''>('')
  const [otherWeek, setOtherWeek] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)

  const colleagues = (users ?? []).filter((u) => u.isActive && u.id !== myId)
  // Kollegaens egne uger - dem kan der byttes med. Ugen, der er i gang, er med, indtil den er slut.
  const theirWeeks = plan.weeks.filter((w) => targetId !== '' && w.user?.id === targetId)

  useEffect(() => setOtherWeek(''), [targetId])

  async function send() {
    if (targetId === '') return
    setError(null)
    try {
      await request.mutateAsync({
        weekStart: week.weekStart,
        targetUserId: targetId,
        otherWeekStart: otherWeek || null,
        message: message.trim() || undefined
      })
      const name = colleagues.find((u) => u.id === targetId)?.displayName ?? 'kollegaen'
      onClose(`Spørgsmålet er sendt til ${name}. Du har vagten, indtil ${name} siger ja.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Spørgsmålet kunne ikke sendes.')
    }
  }

  return (
    <Card title={`🔁 Byt uge ${week.weekNumber} (${weekSpan(week)})`}>
      <div className="stack" style={{ gap: 10 }}>
        <label className="field">
          <span className="field__label">Hvem skal du spørge?</span>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Vælg en kollega ...</option>
            {colleagues.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>
        {targetId !== '' && (
          <label className="field">
            <span>Til gengæld</span>
            <select value={otherWeek} onChange={(e) => setOtherWeek(e.target.value)}>
              <option value="">Kollegaen tager bare ugen</option>
              {theirWeeks.map((w) => (
                <option key={w.weekStart} value={w.weekStart}>
                  Jeg tager kollegaens uge {w.weekNumber} ({weekSpan(w)})
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span className="field__label">Besked (valgfri)</span>
          <input value={message} maxLength={300} onChange={(e) => setMessage(e.target.value)} placeholder="F.eks. jeg skal til bryllup" />
        </label>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button type="button" className="btn btn--primary" disabled={targetId === '' || request.isPending} onClick={() => void send()}>
            {request.isPending ? 'Sender ...' : 'Spørg kollegaen'}
          </button>
          <button type="button" className="btn" onClick={() => onClose()}>
            Annullér
          </button>
        </div>
      </div>
    </Card>
  )
}

function SwapsCard({ swaps, myId, onDone }: { swaps: OnCallSwap[]; myId?: number; onDone: (message: string) => void }) {
  const answer = useAnswerOnCallSwap()
  const [error, setError] = useState<string | null>(null)
  if (swaps.length === 0) return null

  async function act(swap: OnCallSwap, action: 'accept' | 'decline' | 'cancel') {
    setError(null)
    try {
      await answer.mutateAsync({ id: swap.id, action })
      onDone(
        action === 'accept'
          ? 'Byttet er gennemført. Kontoret har fået besked.'
          : action === 'decline'
            ? `${swap.requester.displayName} har fået besked om, at du ikke kan.`
            : 'Spørgsmålet er trukket tilbage.'
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Det kunne ikke gemmes.')
    }
  }

  const describe = (swap: OnCallSwap) => {
    const week = `uge ${swap.weekNumber}`
    return swap.otherWeekNumber ? `${week} mod uge ${swap.otherWeekNumber}` : week
  }

  return (
    <Card title="🔁 Ønsker om at bytte">
      <div className="stack" style={{ gap: 10 }}>
        {swaps.map((swap) => (
          <div key={swap.id} className="device-row stack" style={{ gap: 6 }}>
            <div className="small">
              {swap.target.id === myId ? (
                <>
                  <strong>{swap.requester.displayName}</strong> vil bytte {describe(swap)} med dig.
                </>
              ) : swap.requester.id === myId ? (
                <>
                  Du har spurgt <strong>{swap.target.displayName}</strong> om {describe(swap)}. Venter på svar.
                </>
              ) : (
                <>
                  {swap.requester.displayName} har spurgt {swap.target.displayName} om {describe(swap)}.
                </>
              )}
            </div>
            {swap.message && <div className="tiny muted">"{swap.message}"</div>}
            <div className="btn-group">
              {swap.target.id === myId && (
                <>
                  <button type="button" className="btn btn--sm btn--primary" disabled={answer.isPending} onClick={() => void act(swap, 'accept')}>
                    Ja, byt
                  </button>
                  <button type="button" className="btn btn--sm" disabled={answer.isPending} onClick={() => void act(swap, 'decline')}>
                    Nej
                  </button>
                </>
              )}
              {swap.requester.id === myId && (
                <button type="button" className="btn btn--sm" disabled={answer.isPending} onClick={() => void act(swap, 'cancel')}>
                  Træk tilbage
                </button>
              )}
            </div>
          </div>
        ))}
        {error && <div className="error-box">{error}</div>}
      </div>
    </Card>
  )
}

/** Rækkefølgen og "Fyld planen ud" - kontoret. */
function RotationCard({ rotation, onDone }: { rotation: UserRef[]; onDone: (message: string) => void }) {
  const { data: users } = useUsers()
  const save = useSetOnCallRotation()
  const fill = useFillOnCall()
  const [order, setOrder] = useState<UserRef[]>(rotation)
  const [adding, setAdding] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setOrder(rotation), [rotation])

  const changed = order.map((u) => u.id).join(',') !== rotation.map((u) => u.id).join(',')
  const candidates = (users ?? []).filter((u) => u.isActive && !order.some((o) => o.id === u.id))

  const move = (index: number, by: number) => {
    const next = [...order]
    const [item] = next.splice(index, 1)
    next.splice(index + by, 0, item)
    setOrder(next)
  }

  async function saveOrder() {
    setError(null)
    try {
      await save.mutateAsync(order.map((u) => u.id))
      onDone('Rækkefølgen er gemt. Tryk "Fyld planen ud" for at lægge den på ugerne uden vagt.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rækkefølgen kunne ikke gemmes.')
    }
  }

  async function fillPlan() {
    setError(null)
    try {
      const created = await fill.mutateAsync(26)
      onDone(created === 0 ? 'Alle uger har allerede en vagt.' : `${created} uger har fået en vagt.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Planen kunne ikke fyldes ud.')
    }
  }

  return (
    <Card title="Rækkefølge (kontoret)">
      <div className="tiny muted" style={{ marginBottom: 8 }}>
        Vagten går på omgang i denne rækkefølge. "Fyld planen ud" lægger den på de næste 26 uger, der ikke har en
        vagt - de uger, der allerede er sat, bliver stående, og rækkefølgen fortsætter efter dem.
      </div>
      <div className="stack" style={{ gap: 4 }}>
        {order.map((user, index) => (
          <div key={user.id} className="row row--between device-row" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 8 }}>
              <span className="tiny muted" style={{ width: 18 }}>{index + 1}.</span>
              <Avatar user={user} size={26} />
              <span className="small">{user.displayName}</span>
            </div>
            <div className="btn-group">
              <button type="button" className="btn btn--sm" disabled={index === 0} onClick={() => move(index, -1)} aria-label="Op">
                ↑
              </button>
              <button type="button" className="btn btn--sm" disabled={index === order.length - 1} onClick={() => move(index, 1)} aria-label="Ned">
                ↓
              </button>
              <button type="button" className="btn btn--sm" onClick={() => setOrder(order.filter((o) => o.id !== user.id))} aria-label="Fjern">
                ✕
              </button>
            </div>
          </div>
        ))}
        {order.length === 0 && <div className="small muted">Ingen i rækkefølgen endnu.</div>}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <select
          value={adding}
          aria-label="Tilføj en kollega"
          onChange={(e) => {
            const user = candidates.find((u) => u.id === Number(e.target.value))
            if (user) setOrder([...order, user])
            setAdding('')
          }}
        >
          <option value="">+ Tilføj en kollega ...</option>
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn--primary" disabled={!changed || save.isPending} onClick={() => void saveOrder()}>
          {save.isPending ? 'Gemmer ...' : 'Gem rækkefølgen'}
        </button>
        <button type="button" className="btn" disabled={changed || rotation.length === 0 || fill.isPending} onClick={() => void fillPlan()}>
          {fill.isPending ? 'Fylder ud ...' : 'Fyld planen ud'}
        </button>
      </div>
      {changed && <div className="tiny muted" style={{ marginTop: 6 }}>Gem rækkefølgen, før planen fyldes ud.</div>}
      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
    </Card>
  )
}
